import * as S from '~/modules/design-board/component/shared/CallToActionImageGrid.style';
import { Link } from '@remix-run/react';
import { Grid, Flex, Label, Image, useStyles } from '@archipro-design/aria';
import { useConfigContext } from '@archipro-website/config/bindings/react';
import classNames from 'classnames';

const urlForAsset = (
    assetBaseUrl: string,
    prefix: string,
    type: 'top_small' | 'bottom_small' | 'large',
    fileExtension: string = 'jpg'
) => {
    switch (type) {
        case 'bottom_small':
            return `${assetBaseUrl}/${prefix}_bottom.${fileExtension}`;
        case 'top_small':
            return `${assetBaseUrl}/${prefix}_top.${fileExtension}`;
        case 'large':
            return `${assetBaseUrl}/${prefix}_big.${fileExtension}`;
    }
};

const imagesForPrefix = (assetsBaseUrl: string, prefix: string) => {
    const designBoardBaseUrl = `${assetsBaseUrl}/assets/website/images/design-board`;

    return {
        big: {
            src: urlForAsset(designBoardBaseUrl, prefix, 'large'),
        },
        small: {
            top: {
                src: urlForAsset(designBoardBaseUrl, prefix, 'top_small'),
            },
            bottom: {
                src: urlForAsset(designBoardBaseUrl, prefix, 'bottom_small'),
            },
        },
    };
};

interface GridItem {
    src: string;
    alt?: string;
}

export const CallToActionImageGrid = () => {
    const { css } = useStyles();
    const config = useConfigContext();
    const gridItems: {
        images: {
            big: GridItem;
            small: {
                top: GridItem;
                bottom: GridItem;
            };
        };
        noun: string;
        verb: string;
        href: string;
    }[] = [
        {
            noun: 'products',
            verb: 'shop',
            href: '/products',
            images: imagesForPrefix(config.assetsBaseUrl, 'product'),
        },
        {
            noun: 'projects',
            verb: 'discover',
            href: '/projects',
            images: imagesForPrefix(config.assetsBaseUrl, 'projects'),
        },
        {
            noun: 'professionals',
            verb: 'find',
            href: '/professionals',
            images: imagesForPrefix(config.assetsBaseUrl, 'pro'),
        },
        {
            noun: 'archizeen',
            verb: 'explore',
            href: '/articles',
            images: imagesForPrefix(config.assetsBaseUrl, 'archizeen'),
        },
    ];

    return (
        <Grid
            columns={4}
            variables={{
                gridGap: 'space-20',
            }}
        >
            {gridItems.map((tile, i) => (
                <Link
                    to={tile.href}
                    key={`image-grid-item-${i}`}
                    className={css(S.ImageGridContainer)}
                    prefetch="intent"
                >
                    <Grid columns={`1fr 2fr`} className={css(S.ImageGrid)}>
                        <Grid
                            rows={`1fr 1fr`}
                            columns={`100%`}
                            className={css(S.SmallImagesContainer)}
                        >
                            <div className={css(S.ImageContainer)}>
                                <Image
                                    width={125}
                                    height={125}
                                    alt={tile.images.small.top.alt ?? ''}
                                    src={tile.images.small.top.src}
                                    layout={'fill'}
                                    objectFit={'contain'}
                                />
                            </div>
                            <div className={css(S.ImageContainer)}>
                                <Image
                                    width={125}
                                    height={125}
                                    alt={tile.images.small.bottom.alt ?? ''}
                                    src={tile.images.small.bottom.src}
                                    layout={'fill'}
                                    objectFit={'contain'}
                                />
                            </div>
                        </Grid>
                        <div className={css(S.LargeImagesContainer)}>
                            <div className={css(S.ImageContainer)}>
                                <Image
                                    width={250}
                                    height={250}
                                    alt={tile.images.big.alt ?? ''}
                                    src={tile.images.big.src}
                                    layout={'fill'}
                                    objectFit={'contain'}
                                />
                            </div>
                        </div>
                    </Grid>

                    <Flex
                        column={true}
                        className={classNames(
                            css(S.GridLabel),
                            'cta-image-grid__label-container'
                        )}
                    >
                        <Label variant={'02'} className={css(S.GridLabelVerb)}>
                            {tile.verb}
                        </Label>
                        <Label
                            variant={'02'}
                            className={classNames(
                                css(S.GridLabelNoun),
                                'cta-image-grid__noun-label'
                            )}
                        >
                            {tile.noun}
                        </Label>
                    </Flex>
                </Link>
            ))}
        </Grid>
    );
};
